/* eslint-disable @typescript-eslint/explicit-function-return-type */
import { useMutation, useQuery } from '@tanstack/react-query';
import { DELETE_USER_KEY, GET_USERS_KEY, LINK_USER_GOOGLE_ACCOUNT, LOGOUT_USERS_KEY, UNLINK_USER_GOOGLE_ACCOUNT, UPSERT_USER_KEY } from '../queryConstants';
import { queryDeleteSuccessHandler, queryErrorHandler, queryNoReturnContentSuccessHandler, querySuccessHandler } from '../queryStateHandlers';
import { ISqlEmployee } from '../../../@types/modelV2/rights/user/sqlEmployee';
import { IUserUpsert } from '../../../@types/modelV2/rights/user/userUpsert';
import UserHttpService from '../../../services/right/userHttpService';
import { IGoogleLogInModel } from '../../../@types/modelV2/auth/logIn/GoogleLogInModel';
import AuthHttpService from '../../../services/http/auth/authHttpService';

export const useGetUserByUid = (uid : string) => {
    const query = useQuery<ISqlEmployee, Error>({
        queryKey: [GET_USERS_KEY, uid],
        queryFn: () => UserHttpService.getUserByUid(uid),
        onError: queryErrorHandler('Error loading User.'),
    });

    return query;
};

export const useGetUsers = (isActive ?: boolean | null) => {
    const query = useQuery<Array<ISqlEmployee>, Error>({
        queryKey: [GET_USERS_KEY, isActive],
        queryFn: () => UserHttpService.getUsersList(isActive ?? null),
        onError: queryErrorHandler('Error loading Users.'),
    });

    return query;
};

export const useHandleUpsertUser = () => {
    const result = useMutation<ISqlEmployee, Error, IUserUpsert>({
        mutationKey: [UPSERT_USER_KEY],
        mutationFn: (upsert : IUserUpsert) => UserHttpService.userUpsert(upsert),
        onError: queryErrorHandler('Error creating/updating User.'),
        onSuccess: querySuccessHandler<ISqlEmployee>(GET_USERS_KEY, 'User(s) successfully updated')
    });

    return result;
};

export const useHandleDeleteUser = () => {
    const query = useMutation<void, Error, number>({
        mutationKey: [DELETE_USER_KEY],
        mutationFn: (userId : number) => UserHttpService.userDelete(userId),
        onError: queryErrorHandler('Error deleting User.'),
        onSuccess: queryDeleteSuccessHandler(DELETE_USER_KEY, GET_USERS_KEY, 'User inactivated successfully.'),
    });

    return query;
};

export const useHandleLogoutUsers = () => {
    const result = useMutation<void, Error, Array<number>>({
        mutationKey: [LOGOUT_USERS_KEY],
        mutationFn: (userIds : Array<number>) => AuthHttpService.logoutUsers(userIds),
        onError: queryErrorHandler('Error logging out users.'),
        onSuccess: queryNoReturnContentSuccessHandler('User(s) logged out successfully.')
    });

    return result;
};

export const useHandleLinkGoogleAccount = () => {
    const result = useMutation<ISqlEmployee | null, Error, IGoogleLogInModel>({
        mutationKey: [LINK_USER_GOOGLE_ACCOUNT],
        mutationFn: (googleLoginModel : IGoogleLogInModel) => UserHttpService.linkGoogleAccount(googleLoginModel),
        onError: queryErrorHandler('Error encountered while trying to link Google Account.'),
        onSuccess: queryNoReturnContentSuccessHandler('Google Account linked successfully.')
    });

    return result;
};

export const useHandleUnlinkGoogleAccount = () => {
    const result = useMutation<ISqlEmployee, Error, void>({
        mutationKey: [UNLINK_USER_GOOGLE_ACCOUNT],
        mutationFn: UserHttpService.unlinkGoogleAccount,
        onError: queryErrorHandler('Error encountered while trying to link Google Account.'),
        onSuccess: queryNoReturnContentSuccessHandler('Google Account unlinked successfully.')
    });

    return result;
};
